'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format, isToday, isYesterday } from 'date-fns';
import {
  AlertTriangle,
  Check,
  CheckCheck,
  Headphones,
  Mail,
  MessageCircle,
  Phone,
  Sparkles,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { AvatarWithStatus } from '@kit/chat/components';
import { useConversation, useMessages } from '@kit/chat/hooks';
import { BALLEE_SHORT_NAME, BALLEE_USER_ID } from '@kit/chat/lib';
import type { MessageWithSender } from '@kit/chat/types';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { Button } from '@kit/ui/button';
import { Spinner } from '@kit/ui/spinner';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { AiMessageContent } from './ai-message-content';
import { BookingInquiryCard, readBookingPayload } from './booking-inquiry-card';
import { MessageReactions } from './message-reactions';
import { ToolApprovalCard } from './tool-approval-card';
import { ToolResultCard } from './tool-result-card';

/**
 * True when the message body is nothing but emoji (up to a small cap). Such
 * messages drop the bubble chrome and render large, the way native messengers
 * (iMessage, WhatsApp) do for emoji-only sends.
 */
function isEmojiOnly(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;
  const glyphs = [...trimmed];
  // Long emoji strings keep a bubble — only short reactions go large.
  if (glyphs.length > 8) return false;
  let hasPictographic = false;
  for (const ch of glyphs) {
    if (/\p{Extended_Pictographic}/u.test(ch)) {
      hasPictographic = true;
      continue;
    }
    // Allow emoji glue: ZWJ, variation selector, skin-tone modifiers, keycap
    // digits — all covered by the Emoji_Component property.
    if (/\p{Emoji_Component}/u.test(ch)) continue;
    return false;
  }
  return hasPictographic;
}

interface MessageListProps {
  conversationId: string;
  typingUsers?: Set<string>;
  /** Recently executed tool names for AI progress display */
  activeTools?: string[];
  /**
   * When set (user-facing support view), every incoming message is shown
   * under this single brand identity ("Ballee/Flow Support") with a generic
   * headset avatar — individual super admins are never revealed, whether the
   * sender is the Flow AI or a human agent.
   */
  maskIncomingSenderName?: string | null;
}

export function MessageList({
  conversationId,
  typingUsers,
  activeTools,
  maskIncomingSenderName,
}: MessageListProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const isInitialLoad = useRef(true);
  const client = useSupabase();
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Get current user ID once on mount
  useEffect(() => {
    client.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        setCurrentUserId(user.id);
      }
    });
  }, [client]);

  const {
    messages,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useMessages({ conversationId });

  // Read receipts: a message of mine counts as read once any OTHER
  // participant's last_read_at passes its created_at. Shares the TanStack
  // Query cache with the surrounding conversation view — no extra fetch.
  const { data: conversation } = useConversation(conversationId);
  const othersLastReadAt = useMemo(() => {
    if (!conversation || !currentUserId) return null;
    const timestamps = conversation.participants
      .filter((p) => p.user_id !== currentUserId && p.last_read_at)
      .map((p) => new Date(p.last_read_at as string).getTime());
    return timestamps.length > 0 ? Math.max(...timestamps) : null;
  }, [conversation, currentUserId]);

  // Auto-scroll to bottom on initial load and new messages
  useEffect(() => {
    if (messages.length > 0 && isInitialLoad.current) {
      bottomRef.current?.scrollIntoView();
      isInitialLoad.current = false;
    }
  }, [messages]);

  // Handle scroll to load more
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;

    const { scrollTop } = containerRef.current;
    // Load more when scrolled near top (reversed list)
    if (scrollTop < 100 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Group messages by date
  const groupedMessages = useMemo(() => {
    const groups: { date: string; messages: MessageWithSender[] }[] = [];

    // Messages come in reverse chronological order (newest first)
    // We need to reverse them for display (oldest first within groups)
    const reversedMessages = [...messages].reverse();

    reversedMessages.forEach((message) => {
      const messageDate = new Date(message.created_at);
      let dateLabel: string;

      if (isToday(messageDate)) {
        dateLabel = t('chat:messages.today');
      } else if (isYesterday(messageDate)) {
        dateLabel = t('chat:messages.yesterday');
      } else {
        dateLabel = format(messageDate, 'MMMM d, yyyy');
      }

      const lastGroup = groups[groups.length - 1];
      if (lastGroup && lastGroup.date === dateLabel) {
        lastGroup.messages.push(message);
      } else {
        groups.push({ date: dateLabel, messages: [message] });
      }
    });

    return groups;
  }, [messages, t]);

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-1 items-center justify-center p-8 text-center">
        <p className="text-destructive">
          <Trans i18nKey="chat:messages.loadFailed" />
        </p>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="text-muted-foreground flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
        <p>
          <Trans i18nKey="chat:messages.emptyTitle" />
        </p>
        <p className="text-sm">
          <Trans i18nKey="chat:messages.emptySubtitle" />
        </p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="flex flex-1 flex-col overflow-y-auto overscroll-contain p-4"
    >
      {/* Bottom-anchor: absorbs free space above the thread so short
          conversations hug the composer instead of floating under the
          header. Collapses to zero once messages overflow. */}
      <div aria-hidden="true" className="mt-auto" />

      {/* Load more indicator */}
      {isFetchingNextPage && (
        <div className="flex justify-center pb-4">
          <Spinner className="h-5 w-5" />
        </div>
      )}

      {hasNextPage && !isFetchingNextPage && (
        <div className="flex justify-center pb-4">
          <Button variant="ghost" size="sm" onClick={() => fetchNextPage()}>
            <Trans i18nKey="chat:messages.loadEarlier" />
          </Button>
        </div>
      )}

      {/* Messages grouped by date */}
      {groupedMessages.map((group) => (
        <div key={group.date} className="space-y-4">
          {/* Date separator */}
          <div className="flex items-center justify-center">
            <div className="bg-muted text-muted-foreground rounded-md px-3 py-1 text-xs font-medium">
              {group.date}
            </div>
          </div>

          {/* Messages */}
          {group.messages.map((message, index) => {
            const prevMessage = group.messages[index - 1];
            const showAvatar =
              !prevMessage || prevMessage.sender_id !== message.sender_id;

            // Booking inquiries (metadata.intent_type === 'booking')
            // render as a structured card instead of the standard
            // bubble. Falls back to MessageBubble if the metadata is
            // malformed.
            const bookingPayload = readBookingPayload(message.metadata);
            if (bookingPayload) {
              return (
                <BookingInquiryCard
                  key={message.id}
                  message={message}
                  isOwnMessage={message.sender_id === currentUserId}
                />
              );
            }

            return (
              <MessageBubble
                key={message.id}
                message={message}
                showAvatar={showAvatar}
                currentUserId={currentUserId}
                othersLastReadAt={othersLastReadAt}
                maskIncomingSenderName={maskIncomingSenderName}
              />
            );
          })}
        </div>
      ))}

      {/* Typing indicator */}
      {typingUsers && typingUsers.has(BALLEE_USER_ID) && (
        <TypingIndicator activeTools={activeTools} />
      )}

      {/* Scroll anchor */}
      <div ref={bottomRef} />
    </div>
  );
}

interface MessageBubbleProps {
  message: MessageWithSender;
  showAvatar: boolean;
  currentUserId: string | null;
  /** Latest last_read_at (epoch ms) across the other participants —
   *  drives the sent (single check) vs read (double check) receipt. */
  othersLastReadAt: number | null;
  /** See MessageListProps.maskIncomingSenderName. */
  maskIncomingSenderName?: string | null;
}

/** Extracted reply preview to avoid an IIFE inside JSX */
function ReplyPreview({ replyTo }: { replyTo: unknown }) {
  const { t } = useTranslation();
  const reply = replyTo as {
    sender?: { first_name?: string; last_name?: string | null };
    content?: string;
  };
  const firstName = reply?.sender?.first_name || '';
  const lastName = reply?.sender?.last_name || '';
  const senderName =
    `${firstName} ${lastName}`.trim() || t('chat:panel.unknown');

  return (
    <div className="border-primary bg-muted/50 rounded-t-lg border-l px-3 py-1 text-sm">
      <p className="text-muted-foreground text-xs font-medium">{senderName}</p>
      <p className="text-muted-foreground truncate">
        {reply?.content || t('chat:messages.unavailable')}
      </p>
    </div>
  );
}

function isWhatsAppMessage(metadata: Record<string, unknown> | null): boolean {
  return metadata?.channel === 'whatsapp';
}

/** Get the delivery channel from message delivery_channels array */
function getDeliveryChannel(
  deliveryChannels: unknown,
  metadata: Record<string, unknown> | null,
): 'whatsapp' | 'email' | null {
  // Check delivery_channels array (set by MultiChannelSendService)
  if (Array.isArray(deliveryChannels)) {
    if (deliveryChannels.includes('whatsapp')) return 'whatsapp';
    if (deliveryChannels.includes('email')) return 'email';
  }
  // Fallback: check metadata for inbound WhatsApp messages
  if (metadata?.channel === 'whatsapp' || metadata?.channel_sent)
    return 'whatsapp';
  return null;
}

/** Channel delivery indicator icon */
function ChannelIndicator({
  channel,
  deliveryStatus,
}: {
  channel: 'whatsapp' | 'email';
  deliveryStatus: string;
}) {
  const { t } = useTranslation();
  const isFailed = deliveryStatus === 'failed';

  if (isFailed) {
    return (
      <span
        className="flex items-center gap-0.5 text-red-500"
        title={t('chat:messages.deliveryFailed')}
      >
        <AlertTriangle className="h-3 w-3" />
      </span>
    );
  }

  if (channel === 'whatsapp') {
    return (
      <span
        className="flex items-center gap-0.5 text-green-600 dark:text-green-400"
        title={t('chat:messages.sentViaWhatsApp')}
      >
        <Phone className="h-3 w-3" />
      </span>
    );
  }

  if (channel === 'email') {
    return (
      <span
        className="flex items-center gap-0.5 text-blue-600 dark:text-blue-400"
        title={t('chat:messages.sentViaEmail')}
      >
        <Mail className="h-3 w-3" />
      </span>
    );
  }

  return null;
}

function MessageBubble({
  message,
  showAvatar,
  currentUserId,
  othersLastReadAt,
  maskIncomingSenderName,
}: MessageBubbleProps) {
  const { t } = useTranslation();
  // Check if this is the current user's message using actual user ID
  const isMine = currentUserId ? message.sender_id === currentUserId : false;

  // Flow AI messages always carry their own identity (name, sparkle avatar,
  // AI chip) so users can tell the assistant from a human at a glance.
  const isAiSender = message.sender_id === BALLEE_USER_ID;

  // In the user-facing support view, incoming HUMAN messages share one brand
  // identity — never the individual admin's name or avatar. The AI is
  // deliberately excluded from the mask: it must not pass for a human.
  const isMasked = !!maskIncomingSenderName && !isMine && !isAiSender;

  // Read state: someone other than me has read past this message.
  const isReadByOthers =
    othersLastReadAt !== null &&
    othersLastReadAt >= new Date(message.created_at).getTime();

  // Determine delivery channel (WhatsApp, Email, or none)
  const metadata = message.metadata as Record<string, unknown> | null;
  const deliveryChannel = getDeliveryChannel(
    message.delivery_channels,
    metadata,
  );
  const isFromWhatsApp = isWhatsAppMessage(metadata);

  const firstName = message.sender?.first_name || '';
  const lastName = message.sender?.last_name || '';
  const senderName = isAiSender
    ? BALLEE_SHORT_NAME
    : isMasked
      ? (maskIncomingSenderName as string)
      : `${firstName} ${lastName}`.trim() || t('chat:panel.unknown');
  const avatarUrl =
    isMasked || isAiSender ? null : message.sender?.profile_photo_url;
  const initials = firstName
    ? firstName.slice(0, 1).toUpperCase() +
      (lastName ? lastName.slice(0, 1).toUpperCase() : '')
    : 'UN';

  // Clock time only — the date divider above the group already carries the
  // day, and relative strings ("about 1 hour ago") go stale on screen.
  const timeStr = format(new Date(message.created_at), 'HH:mm');

  if (message.is_deleted) {
    return (
      <div
        className={cn(
          'flex items-start gap-2',
          isMine ? 'flex-row-reverse' : 'flex-row',
        )}
      >
        {!isMine && <div className="w-8" />}
        <div className="bg-muted text-muted-foreground rounded-lg px-4 py-2 italic">
          <Trans i18nKey="chat:messages.deleted" />
        </div>
      </div>
    );
  }

  // Render tool-approval or tool-result content types as special cards
  const contentType = message.content_type;

  if (contentType === 'tool-approval' && metadata?.ai_tool_approval) {
    return (
      <div className="flex items-end gap-2">
        <div className="w-8 flex-shrink-0" />
        <ToolApprovalCard
          messageId={message.id}
          approval={
            metadata.ai_tool_approval as {
              approval_id: string;
              tool_name: string;
              tool_args: Record<string, unknown>;
              risk_level: number;
              action_summary: string;
              status: string;
            }
          }
          timestamp={timeStr}
        />
      </div>
    );
  }

  if (contentType === 'tool-result' && metadata?.ai_tool_result) {
    return (
      <div className="flex items-end gap-2">
        <div className="w-8 flex-shrink-0" />
        <ToolResultCard
          result={
            metadata.ai_tool_result as {
              tool_name: string;
              success: boolean;
              result_summary: string;
              error?: string;
            }
          }
          timestamp={timeStr}
        />
      </div>
    );
  }

  const emojiOnly =
    !!message.content &&
    message.sender_id !== BALLEE_USER_ID &&
    isEmojiOnly(message.content);

  return (
    <div className={cn('flex flex-col', isMine ? 'items-end' : 'items-start')}>
      {/* Sender name (group chats, other people's messages) — sits above the
          avatar+bubble row, indented to clear the avatar gutter. */}
      {showAvatar && !isMine && (
        <p className="text-muted-foreground mb-1 flex items-center gap-1.5 pl-10 text-xs font-medium">
          {senderName}
          {isAiSender && (
            <span className="rounded bg-blue-100 px-1 py-px text-[10px] font-semibold text-blue-700 uppercase dark:bg-blue-900 dark:text-blue-300">
              <Trans i18nKey="chat:inbox.badge.ai" />
            </span>
          )}
        </p>
      )}

      {/* Avatar + bubble row. items-end pins the avatar to the bottom of the
          bubble (not the timestamp), matching iMessage/WhatsApp/Front. */}
      <div
        className={cn(
          'flex max-w-[85%] items-end gap-2',
          isMine ? 'flex-row-reverse' : 'flex-row',
        )}
      >
        {/* Avatar — own messages get neither avatar nor spacer, so the
            bubble sits flush against the panel's right padding. */}
        {!isMine &&
          (showAvatar ? (
            isAiSender ? (
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-500">
                <Sparkles className="h-4 w-4 text-white" />
              </div>
            ) : isMasked ? (
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-500">
                <Headphones className="h-4 w-4 text-white" />
              </div>
            ) : (
              <AvatarWithStatus
                userId={message.sender_id}
                src={avatarUrl ?? undefined}
                name={senderName}
                fallback={initials}
                size="sm"
                className="flex-shrink-0"
              />
            )
          ) : (
            <div className="w-8 flex-shrink-0" />
          ))}

        {/* Bubble column */}
        <div
          className={cn(
            'flex min-w-0 flex-col gap-1',
            isMine ? 'items-end' : 'items-start',
          )}
        >
          {/* Reply preview */}
          {message.reply_to && <ReplyPreview replyTo={message.reply_to} />}

          {/* Message content — emoji-only sends drop the bubble chrome and
              render large, the way native messengers do. */}
          {emojiOnly ? (
            <p className="px-1 text-4xl leading-tight">{message.content}</p>
          ) : (
            <div
              className={cn(
                'rounded-lg px-3.5 py-2',
                isMine
                  ? 'bg-chat-bubble-own text-chat-bubble-own-foreground rounded-br-sm'
                  : 'bg-muted rounded-bl-sm',
              )}
            >
              {message.sender_id === BALLEE_USER_ID ? (
                <AiMessageContent content={message.content ?? ''} />
              ) : (
                <p className="break-words whitespace-pre-wrap">
                  {message.content}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Reactions + metadata — below the row, indented to align under the
          bubble (clears the avatar gutter for incoming messages). */}
      <div
        className={cn(
          'flex flex-col gap-1',
          isMine ? 'items-end' : 'items-start pl-10',
        )}
      >
        <MessageReactions messageId={message.id} />

        <div
          className={cn(
            'text-muted-foreground flex items-center gap-2 text-xs',
            isMine ? 'flex-row-reverse' : 'flex-row',
          )}
        >
          {deliveryChannel ? (
            <ChannelIndicator
              channel={deliveryChannel}
              deliveryStatus={message.delivery_status}
            />
          ) : (
            isFromWhatsApp && (
              <span className="flex items-center gap-0.5 text-green-600 dark:text-green-400">
                <MessageCircle className="h-3 w-3" />
              </span>
            )
          )}
          <span>{timeStr}</span>
          {message.is_edited && (
            <span>
              <Trans i18nKey="chat:messages.edited" />
            </span>
          )}
          {isMine &&
            (isReadByOthers ? (
              <span
                title={t('chat:messages.read')}
                aria-label={t('chat:messages.read')}
              >
                <CheckCheck className="text-primary h-3 w-3" />
              </span>
            ) : (
              <span
                title={t('chat:messages.sent')}
                aria-label={t('chat:messages.sent')}
              >
                <Check className="h-3 w-3" />
              </span>
            ))}
        </div>
      </div>
    </div>
  );
}

/** Tool names that have a dedicated translated label under chat:tools.labels */
const KNOWN_TOOL_KEYS = new Set([
  'searchDancers',
  'getDancerProfile',
  'getMyAssignments',
  'getMyUpcomingShows',
  'searchEvents',
  'getEventDetails',
  'searchProductions',
  'getProductionDetails',
  'searchClients',
  'listVenues',
  'getUnfilledRoles',
  'createEvent',
  'createProduction',
  'getCastAssignments',
  'generateInvoice',
  'searchInvoices',
  'ragSearch',
  'getAnalytics',
]);

/** Tool names that share an existing label key (aliases) */
const TOOL_KEY_ALIASES: Record<string, string> = {
  getUnfilledRolesForEvents: 'getUnfilledRoles',
};

function getToolLabel(toolName: string, t: (key: string) => string): string {
  const aliasedName = TOOL_KEY_ALIASES[toolName] ?? toolName;
  if (KNOWN_TOOL_KEYS.has(aliasedName)) {
    return t(`chat:tools.labels.${aliasedName}`);
  }
  return toolName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

/** Typing indicator bubble for Flow AI with tool progress */
function TypingIndicator({ activeTools }: { activeTools?: string[] }) {
  const { t } = useTranslation();
  const lastTool = activeTools?.length
    ? activeTools[activeTools.length - 1]
    : null;
  const statusText = lastTool
    ? getToolLabel(lastTool, t)
    : t('chat:tools.thinking');

  return (
    <div className="flex items-end gap-2 py-2">
      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-500">
        <Sparkles className="h-4 w-4 text-white" />
      </div>
      <div className="bg-muted rounded-lg rounded-bl-sm px-4 py-2">
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground animate-pulse text-sm">
            {statusText}
          </span>
          <span className="flex gap-0.5">
            <span className="bg-muted-foreground h-1.5 w-1.5 animate-pulse rounded-full [animation-delay:0ms]" />
            <span className="bg-muted-foreground h-1.5 w-1.5 animate-pulse rounded-full [animation-delay:150ms]" />
            <span className="bg-muted-foreground h-1.5 w-1.5 animate-pulse rounded-full [animation-delay:300ms]" />
          </span>
        </div>
        {/* Show completed tools count */}
        {activeTools && activeTools.length > 1 && (
          <p className="text-muted-foreground mt-0.5 text-xs">
            <Trans
              i18nKey="chat:messages.stepsCompleted"
              values={{ count: activeTools.length }}
            />
          </p>
        )}
      </div>
    </div>
  );
}
